// What a batch of cables consumes, for a chosen subset of it.
//
// `/simulation/products` answers with per-cable results AND the batch totals it
// built on the way. Those totals are the whole batch, so taking one cable out of
// the picture meant re-asking the server — and the page had no way to take one
// out at all.
//
// This re-adds them from the per-cable figures instead. It is the same
// arithmetic the server does, keyed the same way, which is what makes the two
// comparable: with every cable selected, the result here equals
// `response.totals` exactly (pinned in tests/consumptionTotals.test.js), so the
// charts do not shift the moment the page starts computing its own numbers.
//
// The copper breakdown is per WIRE GAUGE, not per cable — `single-wire 1.7`,
// `stranded 0.25` — because that is what gets drawn off a drum in the
// warehouse. `copperByWire` on each result carries that cable's share.

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** The key `totals.materials` is grouped by, reproduced from a layer. */
export const materialKeyOf = layer =>
  `${String(layer?.material || '').toLowerCase()} ${String(layer?.type || '').toLowerCase()}`.trim();

/**
 * One cable's materials, grouped by compound rather than by layer.
 *
 * A cable with an inner and an outer sheath of the same PVC shows two layer
 * lines, and the question "how much of this compound does this cable need" has
 * no answer on the card until they are added up. Copper is deliberately NOT in
 * here: it is weighed per gauge, not per compound, and mixing the two would
 * invite adding a copper row to a PVC total.
 */
export function materialsOfCable(cable) {
  const out = new Map();
  for (const layer of cable?.layers || []) {
    const key = materialKeyOf(layer);
    if (!key) continue;
    const prev = out.get(key) || { key, material: layer.material, type: layer.type, kg: 0, layers: [] };
    prev.kg += num(layer.quantityTotal);
    prev.layers.push(String(layer.layer || ''));
    out.set(key, prev);
  }
  return [...out.values()].sort((a, b) => b.kg - a.kg);
}

/**
 * Batch totals over the cables whose index is selected.
 *
 * @param result    `response.result` — one entry per cable
 * @param selected  a Set of indices, or null/undefined for "all of them"
 */
export function totalsForSelection(result = [], selected = null) {
  const copper = {};
  const materials = {};
  let copperKg = 0;
  let lengthM = 0;
  let cables = 0;

  result.forEach((cable, index) => {
    // A cable the simulation could not resolve carries an `error` and no
    // figures; counting it would report a batch bigger than the one priced.
    if (cable?.error) return;
    if (selected && !selected.has(index)) return;

    cables += 1;
    copperKg += num(cable.copperWeightTotalKg);
    lengthM += num(cable.lengthMeters);

    for (const [key, kg] of Object.entries(cable.copperByWire || {})) {
      copper[key] = num(copper[key]) + num(kg);
    }
    for (const layer of cable.layers || []) {
      const key = materialKeyOf(layer);
      if (!key) continue;
      materials[key] = num(materials[key]) + num(layer.quantityTotal);
    }
  });

  return { copper, materials, copperKg, lengthM, cables };
}

/**
 * Is this response old enough to predate the per-cable copper breakdown?
 *
 * Without `copperByWire` the copper chart cannot be rebuilt for a subset — the
 * response only carries one scalar per cable — and a chart that silently shows
 * the whole batch while the page says a cable is deselected would be worse than
 * no chart. The page says so instead.
 */
export const hasCopperBreakdown = (result = []) =>
  result.some(c => c && !c.error && c.copperByWire && Object.keys(c.copperByWire).length > 0);

export default totalsForSelection;
